// @ts-nocheck
import { SortAscendingIcon, SortDescendingIcon } from 'components/icons';
import {
  FavoriteToggleCell,
  LinkToSegmentCell,
  loadingStateDataPlaceholder,
  Pagination,
  SegmentSyncCell,
  TableHeaders,
} from 'components/segments/MySegments';
import { Skeleton, Tooltip } from 'components/ui';
import querystring from 'querystring';
import { useEffect, useMemo, useRef } from 'react';
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import { useAppSelector } from 'store';
import {
  useGetCustomerSegmentsQuery,
  useUpdateSegmentMutation,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import { useParseURLQueryParams } from 'utils';

export default function MySegmentsTable() {
  const { query } = useParseURLQueryParams();

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { currentStore } = useAppSelector(userDetailsState);

  const { data: segments, isLoading: loading } = useGetCustomerSegmentsQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );

  const locale = currentStore.completeInfo?.primary_locale ?? 'en';

  const [updateSegment] = useUpdateSegmentMutation();

  const skipPageResetRef = useRef(false);

  /**
   * UPDATES FAVORITE AND AUTOSYNC
   *  Function that updates the state while making the API call - Function passed directly to the table instance
   * @param {{rowIndex:number, columnId:string, value:boolean, segmentId:number}} param0
   */
  const updateTogglesData = async ({
    rowIndex,
    columnId,
    value,
    segmentId,
  }) => {
    // Set skip pageReset to avoid clearing filters and sorting
    skipPageResetRef.current = true;

    const body = {
      [columnId]: value.toString(),
    };
    updateSegment({ body, segmentId, serializedStoreName: queryParams });
  };

  // After the table has updated by the updateTogglesData function, set skipPageReset back to false
  useEffect(() => {
    skipPageResetRef.current = false;
  });

  const columns = useMemo(
    () => [
      {
        Header: '',
        accessor: 'favorite',
        Cell: FavoriteToggleCell,
        sortType: 'basic',
        sortDescFirst: true,
      },
      {
        Header: () => <p className="text-left">Segment</p>,
        accessor: 'segment_name',
        Cell: LinkToSegmentCell,
        sortDescFirst: true,
      },
      {
        Header: 'Users',
        accessor: 'segment_size',
        Cell: ({ value }) => <p>{value?.toLocaleString(locale)}</p>,
        sortDescFirst: true,
      },
      {
        Header: () => (
          <Tooltip text="Customer lifetime value, representing the total amount of money a customer is expected to spend throughout their lifetime with your store. Forward-looking two years, inclusive of current spend.">
            <p>CLV</p>
          </Tooltip>
        ),
        accessor: 'aclv',
        Cell: ({ value }) => (
          <p>
            {value?.toLocaleString(locale, {
              style: 'currency',
              currency: currentStore.completeInfo?.currency ?? 'usd',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </p>
        ),
        sortDescFirst: true,
      },
      {
        Header: () => (
          <Tooltip text="Average number of orders per user, calculated using non-fully-refunded orders and users with at least one purchase.">
            <p>Frequency</p>
          </Tooltip>
        ),
        accessor: 'ano',
        sortDescFirst: true,
      },
      {
        Header: () => (
          <Tooltip text='Average order value, a.k.a. average "basket size" of orders. Calculated using net revenue and all non-fully-refunded orders.'>
            <p>AOV</p>
          </Tooltip>
        ),
        accessor: 'aov',
        Cell: ({ value }) => (
          <p>
            {value?.toLocaleString(locale, {
              style: 'currency',
              currency: currentStore.completeInfo?.currency ?? 'usd',
            })}
          </p>
        ),
        sortDescFirst: true,
      },
      {
        Header: () => (
          <Tooltip text="How much of your net revenue you keep as gross profit, calculated as gross profit / net revenue.">
            <p>Gross margin</p>
          </Tooltip>
        ),
        accessor: 'gross_margin',
        Cell: ({ value }) => (
          <p>{value ? (value * 100).toFixed() + '%' : '--'}</p>
        ),
        sortDescFirst: true,
      },
      {
        Header: () => (
          <Tooltip text="Average click-through rate on opened emails.">
            <p>ACR</p>
          </Tooltip>
        ),
        accessor: 'acr',
        Cell: ({ value }) => (
          <p>{value ? (value * 100).toFixed(2) + '%' : '--'}</p>
        ),
        sortDescFirst: true,
      },

      {
        Header: 'Integrations',
        id: 'integrations',
        Cell: SegmentSyncCell,
        sortType: 'basic',
        sortDescFirst: true,
      },
    ],
    [currentStore.completeInfo?.currency, locale]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { globalFilter, pageIndex, pageSize },
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
  } = useTable(
    {
      columns,
      data: segments ?? loadingStateDataPlaceholder,
      autoResetSortBy: !skipPageResetRef.current,
      autoResetGlobalFilter: !skipPageResetRef.current,
      autoResetPage: !skipPageResetRef.current,
      updateTogglesData,

      skipPageResetRef: skipPageResetRef,
      initialState: {
        pageIndex: 0,
        pageSize: 20, // * Arbitrary amount defined
        sortBy: [
          { id: 'favorite', desc: true },
          { id: 'aclv', desc: true },
        ],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  return (
    <>
      <TableHeaders
        globalFilter={globalFilter}
        pageSize={pageSize}
        setGlobalFilter={setGlobalFilter}
        setPageSize={setPageSize}
      />

      <table
        {...getTableProps()}
        className="w-full mt-8 table-auto text-segments-13px"
      >
        <thead className="border-b border-black select-none">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={`px-2 py-2.5 whitespace-nowrap ${
                    column.isSorted &&
                    'bg-segments-accent-color-1 bg-opacity-5 text-segments-link'
                  }`}
                  title=""
                >
                  {column.isSorted ? (
                    <div
                      className={`flex items-end ${
                        column.id !== ('segment_name' || 'favorite') &&
                        'justify-end'
                      } `}
                    >
                      {column.render('Header')}

                      {column.isSortedDesc ? (
                        <SortDescendingIcon className="w-4 h-4 ml-1" />
                      ) : (
                        <SortAscendingIcon className="w-4 h-4 ml-1" />
                      )}
                    </div>
                  ) : (
                    <>{column.render('Header')}</>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                className="text-base border-b odd:bg-segments-gray-F1 hover:bg-segments-accent-green-3"
              >
                {row.cells.map((cell) => {
                  return (
                    <td
                      {...cell.getCellProps()}
                      className={`py-1.5 text-center px-2 ${
                        cell.column.isSorted &&
                        'bg-segments-accent-color-1 bg-opacity-5 text-segments-link'
                      }`}
                    >
                      <Skeleton isLoaded={!!!loading}>
                        {cell.render('Cell')}
                      </Skeleton>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        pages={pageOptions}
        currentPage={pageIndex}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        nextPage={nextPage}
        previousPage={previousPage}
        gotoPage={gotoPage}
      />
    </>
  );
}
